import {PATCH_HEADERS} from 'api/constants';
import {IUseGetExportFileResponse} from 'api/fileExport';
import {
    CARD_TRANSACTIONS_DETAILS_CACHE,
    CARD_TRANSACTION_PUBLIC_CACHE,
    EXPENSE_CATEGORIES,
    EXPENSE_CONFIGURATION,
    EXPENSE_COST_CENTERS,
    EXPENSE_VATS,
    EXPENSE_VATS_BATCH,
    TRANSACTIONS_CACHE,
    TRANSACTION_DETAILS_CACHE,
} from 'constants/cache';
import {useClient} from 'context/auth-context';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
    ExpenseCategory,
    ExpenseConfiguration,
    ExpenseCostCenter,
    UpdateExpenseOnCardTransactionDetails,
} from 'types/api/expenses';
import {saveFile} from 'helpers/fileSaver';

const useGetExpenseCategories = (
    disabled?: boolean,
    area: string = 'default',
) => {
    const client = useClient();

    return useQuery<ExpenseCategory[]>({
        queryKey: [EXPENSE_CATEGORIES],
        queryFn: () =>
            client('/web_api/expense_categories', {
                params: {
                    visible: true,
                    pagination: false,
                    area,
                },
            }),
        enabled: !disabled,
    });
};

const useGetExpenseCostCenters = (disabled?: boolean) => {
    const client = useClient();

    return useQuery<ExpenseCostCenter[]>({
        queryKey: [EXPENSE_COST_CENTERS],
        queryFn: () =>
            client('/web_api/expense_cost_centers', {
                params: {
                    visible: true,
                    pagination: false,
                },
            }),
        enabled: !disabled,
    });
};

const useUpdateExpenseCategories = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<ExpenseCategory, unknown, ExpenseCategory>({
        mutationFn: request =>
            client(request['@id'], {
                ...PATCH_HEADERS,
                data: request,
                method: 'PATCH',
            }),
        onSuccess: category => {
            queryClient.invalidateQueries({queryKey: [EXPENSE_CATEGORIES]});
        },
    });
};

const useUpdateExpenseCostCenter = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<ExpenseCostCenter, unknown, ExpenseCostCenter>({
        mutationFn: request =>
            client(request['@id'], {
                ...PATCH_HEADERS,
                data: request,
                method: 'PATCH',
            }),
        onSuccess: costCenter => {
            queryClient.invalidateQueries({
                queryKey: [EXPENSE_COST_CENTERS],
            });
        },
    });
};

const useCreateExpenseCategory = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<
        ExpenseCategory,
        unknown,
        Pick<
            ExpenseCategory,
            | 'name'
            | 'accountingAccountNumber'
            | 'businessPartner'
            | 'visible'
            | 'externalId'
        >
    >({
        mutationFn: request =>
            client('/web_api/expense_categories', {
                method: 'POST',
                data: request,
            }),
        onSuccess: category => {
            queryClient.cancelQueries({queryKey: [EXPENSE_CATEGORIES]});
            queryClient.setQueryData(
                [EXPENSE_CATEGORIES],
                (prev: ExpenseCategory[]) => [...prev, category],
            );
            queryClient.invalidateQueries({queryKey: [EXPENSE_CATEGORIES]});
        },
    });
};

export type ICreateExpenseCategoriesBatchRequest = Pick<
    ExpenseCategory,
    'name' | 'accountingAccountNumber' | 'businessPartner' | 'visible'
> & {externalId: string};

const useCreateExpenseCategoriesBatch = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<
        ExpenseCategory,
        unknown,
        ICreateExpenseCategoriesBatchRequest[]
    >({
        mutationFn: request =>
            client('/web_api/expense_categories_batch', {
                method: 'POST',
                data: request,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: [EXPENSE_CATEGORIES]});
        },
    });
};

const useCreateExpenseCostCenter = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<
        ExpenseCostCenter,
        unknown,
        Pick<
            ExpenseCostCenter,
            'name' | 'number' | 'businessPartner' | 'visible'
        >
    >({
        mutationFn: request =>
            client('/web_api/expense_cost_centers', {
                method: 'POST',
                data: request,
            }),
        onSuccess: costCenter => {
            queryClient.cancelQueries({queryKey: [EXPENSE_COST_CENTERS]});
            queryClient.setQueryData(
                [EXPENSE_COST_CENTERS],
                (prev: ExpenseCostCenter[]) => [...prev, costCenter],
            );
            queryClient.invalidateQueries({
                queryKey: [EXPENSE_COST_CENTERS],
            });
        },
    });
};

const useDeleteExpenseCategory = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<unknown, unknown, string>({
        mutationFn: expenseIri =>
            client(expenseIri, {
                method: 'DELETE',
                data: {},
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: [EXPENSE_CATEGORIES]});
        },
    });
};

const useDeleteExpenseCostCenter = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<unknown, unknown, string>({
        mutationFn: expenseIri =>
            client(expenseIri, {
                method: 'DELETE',
                data: {},
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [EXPENSE_COST_CENTERS],
            });
        },
    });
};

const useUpdateExpenseOnCardTransactionDetails = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<unknown, unknown, UpdateExpenseOnCardTransactionDetails>(
        {
            mutationFn: ({'@id': iri, ...rest}) =>
                client(`${iri}/expense_management`, {
                    data: rest,
                    method: 'PATCH',
                    ...PATCH_HEADERS,
                }),
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [CARD_TRANSACTIONS_DETAILS_CACHE],
                });
                queryClient.invalidateQueries({
                    queryKey: [TRANSACTION_DETAILS_CACHE],
                });
            },
        },
    );
};

interface IUseDownloadExpenseDocument {
    documentIri: string;
}

const useDownloadExpenseDocument = () => {
    const client = useClient();

    return useMutation<
        IUseGetExportFileResponse,
        unknown,
        IUseDownloadExpenseDocument
    >({
        mutationFn: ({documentIri}) =>
            client(
                `/web_api/card_transaction_details/download/${documentIri
                    .split('/')
                    .pop()}`,
                {
                    method: 'GET',
                },
            ),
        onSuccess: (data: IUseGetExportFileResponse) => {
            saveFile(data);
        },
    });
};
interface IUsePublicDownloadExpenseDocument {
    hash: string;
}

const usePublicDownloadExpenseDocument = () => {
    const client = useClient();

    return useMutation<
        IUseGetExportFileResponse,
        unknown,
        IUsePublicDownloadExpenseDocument
    >({
        mutationFn: ({hash}) =>
            client(`/web_api/documents/public_download/${hash}`, {
                method: 'GET',
            }),
        onSuccess: (data: IUseGetExportFileResponse) => {
            saveFile(data);
        },
    });
};

interface IUseDeleteExpenseDocument {
    transactionDetailsIri: string;
    documentIri: string;
}

const useDeleteExpenseDocument = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            transactionDetailsIri,
            documentIri,
        }: IUseDeleteExpenseDocument) =>
            client(
                `${transactionDetailsIri}/documents/${documentIri
                    .split('/')
                    .pop()}`,
                {
                    method: 'DELETE',
                    data: {},
                },
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: [TRANSACTIONS_CACHE]});
            queryClient.invalidateQueries({
                queryKey: [TRANSACTION_DETAILS_CACHE],
            });
        },
    });
};

const usePublicUpdateExpenseOnCardTransactionDetails = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<unknown, unknown, UpdateExpenseOnCardTransactionDetails>(
        {
            mutationFn: ({'@id': _, hash, ...rest}) =>
                client(
                    `/web_api/card_transaction_details_public/${hash}/expense_management`,
                    {
                        data: rest,
                        method: 'PATCH',
                        ...PATCH_HEADERS,
                    },
                ),
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [CARD_TRANSACTION_PUBLIC_CACHE],
                });
            },
        },
    );
};

export interface IExpenseVat {
    '@context': '/contexts/ExpenseVAT';
    '@id': string;
    '@type': 'ExpenseVAT';
    businessPartner: string;
    value: number;
    name: string;
    externalId: string;
}

const useGetExpenseVats = (needsExpenseVat?: boolean) => {
    const client = useClient();

    return useQuery<IExpenseVat[]>({
        queryKey: [EXPENSE_VATS],
        queryFn: () =>
            client('/web_api/expense_vats', {
                params: {
                    pagination: false,
                    visible: true,
                },
            }),
        enabled: needsExpenseVat,
    });
};

export interface ICreateExpenseVat {
    businessPartner: string;
    value: number;
    name: string;
    externalId?: string;
}

const useCreateExpenseVat = () => {
    const queryClient = useQueryClient();
    const client = useClient();

    return useMutation<IExpenseVat, unknown, ICreateExpenseVat>({
        mutationFn: data =>
            client('/web_api/expense_vats', {
                data,
                method: 'POST',
            }),
        onSuccess: () =>
            queryClient.invalidateQueries({queryKey: [EXPENSE_VATS]}),
    });
};

const useCreateExpenseVatBatch = () => {
    const queryClient = useQueryClient();
    const client = useClient();

    return useMutation<IExpenseVat, unknown, ICreateExpenseVat[]>({
        mutationFn: data =>
            client('/web_api/expense_vats_batch', {
                data,
                method: 'POST',
            }),
        onSuccess: () =>
            queryClient.invalidateQueries({queryKey: [EXPENSE_VATS_BATCH]}),
    });
};

const useUpdateExpenseVat = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<
        IExpenseVat,
        unknown,
        Omit<IExpenseVat, '@type' | '@context'>
    >({
        mutationFn: ({'@id': iri, ...rest}) =>
            client(iri, {
                data: rest,
                method: 'PATCH',
                ...PATCH_HEADERS,
            }),
        onSuccess: () =>
            queryClient.invalidateQueries({queryKey: [EXPENSE_VATS]}),
    });
};

const useDeleteExpenseVat = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<unknown, unknown, IExpenseVat>({
        mutationFn: data =>
            client(data['@id'], {
                method: 'DELETE',
                data: {},
            }),
        onSuccess: () =>
            queryClient.invalidateQueries({queryKey: [EXPENSE_VATS]}),
    });
};

const useGetExpenseManagementConfiguration = () => {
    const client = useClient();

    return useQuery<ExpenseConfiguration[]>({
        queryKey: [EXPENSE_CONFIGURATION],
        queryFn: () => client('/web_api/expense_management_configurations', {}),
    });
};

const useUpdateExpenseManagementConfiguration = () => {
    const client = useClient();
    const queryClient = useQueryClient();

    return useMutation<ExpenseConfiguration, unknown, ExpenseConfiguration>({
        mutationFn: data =>
            client(data['@id'], {
                method: 'PATCH',
                data,
                ...PATCH_HEADERS,
            }),
        onSuccess: () =>
            queryClient.invalidateQueries({queryKey: [EXPENSE_CONFIGURATION]}),
    });
};

export {
    useGetExpenseCategories,
    useUpdateExpenseCategories,
    useCreateExpenseCategory,
    useCreateExpenseCategoriesBatch,
    useUpdateExpenseOnCardTransactionDetails,
    useDeleteExpenseCategory,
    useDownloadExpenseDocument,
    useDeleteExpenseDocument,
    usePublicDownloadExpenseDocument,
    usePublicUpdateExpenseOnCardTransactionDetails,
    useGetExpenseCostCenters,
    useCreateExpenseCostCenter,
    useUpdateExpenseCostCenter,
    useDeleteExpenseCostCenter,
    useGetExpenseVats,
    useCreateExpenseVat,
    useUpdateExpenseVat,
    useDeleteExpenseVat,
    useCreateExpenseVatBatch,
    useGetExpenseManagementConfiguration,
    useUpdateExpenseManagementConfiguration,
};
